import RpIcon from "@/components/RpIcon";
import API from '@/constant/API';
import { INPUT_THROTTLE_MS } from '@/constant/Constant';
import useToggle from '@/hook/useToggle';
import classes from "@/pages/security/css/index.module.less";
import { getTaskId, setTmpToken } from '@/redux/slices/user';
import axiosInstance from '@/utils/axios';
import { execYDRecaptcha, initYDRecaptcha } from '@/utils/ydRecaptcha';
import { Box, Button, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { debounce } from 'es-toolkit';
import PropTypes from 'prop-types';
import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppModal from './AppModal';
import Countdown from './Countdown';
import { fbLogEventDefaultParams, FB_EVENT_TYPES } from '@/utils/firebase';


enum SEND_CODE_TYPE {
  MOBILE,
  EMAIL,
}

const countdownSecFormat = (time) => {
  const hours = 60 * 60 * 1000;
  const minutes = 60 * 1000;

  const h = Math.floor(time / hours);
  const m = Math.floor((time - h * hours) / minutes);
  const s = Math.floor((time - h * hours - m * minutes) / 1000);
  return (
    <span>
      {s}S
    </span>
  );
};

// ----------------------------------------------------------------------

const COUNT_DOWN = 60;

const SendCodeButton = forwardRef(({call, action, type, params, btnText, enable, useRecaptcha,pageName}, ref) => {
  const { t } = useTranslation();
  const [inCountdown, setInCountdown] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [countdownTarget, setCountdownTarget] = useState(null);
  const [channels, setChannels] = useState([]);

  const {
    toggle: mobileChannelOpen,
    onToggle: onToggleMobileChannelOpen,
  } = useToggle(false);

  useImperativeHandle(ref, () => (
    {
      startCountdown,
      stopCountdown,
    }
  ));

  const stopCountdown = () => {
    setInCountdown(false);
  }

  const sendVerifyCode = async () => {
    _fbLogEvent(FB_EVENT_TYPES.GET_CODE_CLICK)
    if (call) {
      if (await call()) {
        _fbLogEvent(FB_EVENT_TYPES.GET_CODE_SUCCESS)

        startCountdown();
      }
    } else {
      if (useRecaptcha) {
        // 使用易盾行为验证码
        _debounceGetYidunRecaptchaToken();
      } else {
        if (type == SEND_CODE_TYPE.MOBILE) {
          _checkMobileChannel();
          return;
        }
        _doSendCode(null, null);
      }
    }
  };

  const _checkMobileChannel = async () => {
    const res = await axiosInstance.post(API.SEND_MOBILE_CHANNEL_LIST, {
      action: action,
      taskId: getTaskId(),
    });

    setChannels(res);
    onToggleMobileChannelOpen();
  }

  const _fbLogEvent = (type) => {
    if (pageName) {
      fbLogEventDefaultParams(type, { pageName });
    }
  };

  const _getYidunRecaptchaToken = async () => {
    initYDRecaptcha(execYDRecaptcha, _onRecaptchaVerified);
  };
  const _debounceGetYidunRecaptchaToken = useCallback(debounce(_getYidunRecaptchaToken, INPUT_THROTTLE_MS), [_getYidunRecaptchaToken]);
  
  const _onRecaptchaVerified = async (token) => {
    setRecaptchaToken(token);
    if (type == SEND_CODE_TYPE.MOBILE) {
      _checkMobileChannel();
      return;
    }
    _doSendCode(token, null);
  }

  const _doSendCode = async (token, channel) => {
    const requestParams = params ?? {};
    requestParams['action'] = action;
    if (token || recaptchaToken) {
      requestParams['captchaToken'] = token ?? recaptchaToken;
    }

    const isMobile = type === SEND_CODE_TYPE.MOBILE;
    if (isMobile) {
      requestParams['channelType'] = channel ?? 'sms';
    }

    try {
      const res = await axiosInstance.post(isMobile ? API.SEND_MOBILE_CODE : API.SEND_EMAIL_CODE, requestParams);
      if (res != null) {
        // set tmp token.
        setTmpToken(res);
      }
      _fbLogEvent(FB_EVENT_TYPES.GET_CODE_SUCCESS);
      
      startCountdown();
    } catch(err) {}
  }

  const startCountdown = () => {
    setInCountdown(true);
    const target = new Date();
    target.setTime(target.getTime() + COUNT_DOWN * 1000);
    setCountdownTarget(target);
  }

  const _onChannelClick = (evt, channel) => {
    evt.stopPropagation();
    onToggleMobileChannelOpen();
    _doSendCode(null, channel);
  }

  const _getChannelTitle = (channel) => {
    return t(`mobile_channel_${channel}`);
  }

  const _createChannelsList = () => {
    if (!channels.length) return null;

    return <Box className={classes["model-wrap"]}>
      <Box className={classes["model-title-wrap"]}>
        <Box onClick={onToggleMobileChannelOpen}><RpIcon icon="close" size={16}/></Box>
        <Box className={classes["title"]}>
          {t('choose_channel')}
        </Box>
      </Box>
      <List>
        {channels.map((channel,index) => <ListItem key={index} disablePadding>
          <ListItemButton onClick={(evt) => _onChannelClick(evt, channel)}>
            <ListItemIcon>
              {channel === 'sms' && <RpIcon icon="phone" size={22} />}
              {channel === 'smsAliyunWhatsApp' && <RpIcon icon="whatsapp" size={22} />}
            </ListItemIcon>
            <ListItemText primary={_getChannelTitle(channel)}></ListItemText>
          </ListItemButton>
        </ListItem>)}
      </List>
    </Box>
  }

  return (
    <Button className="SendCodeButton" color="primary" variant='text' component="span" disableRipple
            onClick={sendVerifyCode} disabled={!enable || inCountdown}>{inCountdown ?
      <Countdown seconds={60} onEnd={() => setInCountdown(false)} /> : btnText}

      <AppModal open={mobileChannelOpen} onClose={onToggleMobileChannelOpen}>
        {_createChannelsList()}
      </AppModal>
    </Button>
  );
});

SendCodeButton.propTypes = {
  btnText: PropTypes.string,
  enable: PropTypes.bool,
}
SendCodeButton.displayName = 'SendCodeButton';
export default SendCodeButton;
